/* ==========================================================================
   Makefield Putters — custom theme behaviour
   --------------------------------------------------------------------------
   Loaded deferred, after Dawn's own scripts.

   1. Header height sync  — keeps --mk-header-height matching the real header.
   2. Sticky solid state  — swaps the transparent header to solid the moment
                            it pins to the top of the viewport.

   Dawn's own sticky-header JS is switched off for this section
   (sticky_header_type: "none"), because neither of its modes matches the
   design: "always" pins the header from first paint — covering the
   announcement bar — and "on-scroll-up" only reveals on upward scroll. The
   header instead uses `position: sticky` (see custom.css), and the classes
   below track when it engages.
   ========================================================================== */

(function () {
  'use strict';

  var SOLID = 'mk-header--solid';
  var SENTINEL_ID = 'mk-sticky-sentinel';

  function header() {
    return document.querySelector('.section-header');
  }

  /* Keep --mk-header-height in sync with the rendered header.
     On the homepage the first section is pulled up by this value so the hero
     sits behind the header; if it drifts from the real height (nav wrapping, a
     taller uploaded logo, webfont swap) the hero would sit slightly wrong. */
  function syncHeaderHeight() {
    var el = header();
    if (!el) return;

    var height = el.offsetHeight;
    if (height > 0) {
      document.documentElement.style.setProperty('--mk-header-height', height + 'px');
    }
  }

  /* Toggle the solid background exactly when the header pins.

     The header rests below the announcement bar and only pins once that bar
     has scrolled away, so the trigger point is the height of everything above
     the header. A sentinel spanning that distance is watched: while any of it
     is still on screen the header has not pinned yet and stays transparent;
     when it is fully gone the header is pinned, so it turns solid.

     An IntersectionObserver fires only when the threshold is crossed, unlike a
     scroll listener which would run on every frame. */
  function initStickyState() {
    var el = header();
    if (!el || !el.parentNode) return;

    // Only the homepage has a transparent state to switch out of.
    if (!document.body.classList.contains('mk-header-overlay')) return;

    var existing = document.getElementById(SENTINEL_ID);
    if (existing) existing.remove();

    /* The sentinel is inserted directly BEFORE the header in the DOM and left
       at its natural position in the flow, with zero height.

       Measuring a pixel offset was tried and is unreliable here: the header is
       `position: sticky` (so its own top reads 0 once pinned) and the
       announcement bar can still be 0px tall when this first runs, which
       collapsed the offset to zero and left the header permanently solid.
       Anchoring to a real DOM position needs no measurement at all — the
       sentinel scrolls out of view exactly when the header pins. */
    /* How far the page scrolls before the header reaches the top: the height
       of everything above it (the announcement bar). Measured from the
       previous siblings, not from the header itself — the header is
       `position: sticky`, so once pinned its own top reads 0. */
    var offset = 0;
    var sibling = el.previousElementSibling;
    while (sibling) {
      if (sibling.id !== SENTINEL_ID) offset += sibling.offsetHeight || 0;
      sibling = sibling.previousElementSibling;
    }

    // Nothing above the header: it is pinned from the start, so there is no
    // transparent state to manage.
    if (offset <= 0) {
      el.classList.add(SOLID);
      return;
    }

    var sentinel = document.createElement('div');
    sentinel.id = SENTINEL_ID;
    sentinel.setAttribute('aria-hidden', 'true');

    /* IMPORTANT: Dawn's base.css contains `div:empty { display: none }`, which
       silently collapses an empty sentinel to zero height. A zero-height,
       display:none element never intersects, so the observer would report
       "not visible" from first paint and the header would be stuck solid.
       The zero-width space keeps the div non-empty; `display:block` is set
       explicitly as a second guard. */
    sentinel.textContent = '​';
    sentinel.style.cssText =
      'display:block;position:absolute;top:0;left:0;width:100%;' +
      'height:' + offset + 'px;pointer-events:none;visibility:hidden;';

    document.body.appendChild(sentinel);

    if (!('IntersectionObserver' in window)) {
      // Without observer support, keep the header readable rather than
      // permanently transparent.
      el.classList.add(SOLID);
      return;
    }

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          el.classList.toggle(SOLID, !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    ).observe(sentinel);
  }

  var resizeTimer;
  function onResize() {
    window.clearTimeout(resizeTimer);
    // The announcement bar can wrap to a second line at narrow widths, which
    // changes where the header pins — so the sentinel is rebuilt too.
    resizeTimer = window.setTimeout(function () {
      syncHeaderHeight();
      initStickyState();
    }, 150);
  }

  function init() {
    syncHeaderHeight();
    initStickyState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('resize', onResize);

  // Webfonts loading can change the header height after first paint.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(syncHeaderHeight);
  }

  // Re-initialise when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
